import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Bookmark, Boxes, Folder, Search, Tag, X, type LucideIcon } from "lucide-react";
import { useIsTabActive } from "@/contexts/TabActivityContext";
import { useDebounce } from "@/hooks/useDebounce";
import { useSearchStashes } from "@/hooks/useSearchStashes";
import { DEFAULT_RECENT_POSTS_PAGE_SIZE } from "@/hooks/useRecentStashes";
import { collectionDisplayTitle } from "@/hooks/useCollections";
import { useEntityCard } from "@/features/entity/useEntityDetail";
import { entityImageUrl } from "@/features/entity/entityImage";
import { isSuggestedTag } from "@/lib/tagScope";
import { COLLECTION_DETAIL, SEARCH, STASH_DETAIL, STASHES, STUFF_DETAIL } from "@/routes";
import { useSearchCatalogMatches, useStuffSearch } from "./useSearchSources";
import { SEARCH_DEBOUNCE_MS } from "./searchModel";
import "./SearchField.css";
import { CAPTION, META, ROW_TITLE } from "@/lib/typography";
import { cn } from "@/lib/utils";

export interface SearchFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: (query: string) => void;
  placeholder?: string;
  label?: string;
  focusKey?: string;
  disabledSuggestions?: boolean;
  pageSize?: number;
}

/** One query filters the current page; submission opens complete account results. */
export function SearchField({ value, onChange, onSubmit, placeholder = "Search Stasht", label = "Search Stasht", focusKey, disabledSuggestions = false, pageSize = DEFAULT_RECENT_POSTS_PAGE_SIZE }: SearchFieldProps) {
  const navigate = useNavigate();
  const tabActive = useIsTabActive();
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>();
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const query = value ?? draft;
  const term = query.trim();
  const debounced = useDebounce(term, SEARCH_DEBOUNCE_MS);
  const expanded = open && tabActive && !disabledSuggestions && !!term;
  const lookupReady = expanded && debounced === term;
  useEffect(() => { if (focusKey && tabActive) input.current?.focus(); }, [focusKey, tabActive]);
  useEffect(() => { if (!tabActive) input.current?.blur(); }, [tabActive]);
  const change = (next: string) => {
    if (value === undefined) setDraft(next);
    onChange?.(next);
    setActiveId(undefined);
    setOpen(true);
  };
  const submit = () => {
    if (!term) return;
    setOpen(false);
    setActiveId(undefined);
    input.current?.blur();
    if (onSubmit) onSubmit(query);
    else navigate(`${SEARCH()}?q=${encodeURIComponent(query)}`);
  };
  const choose = (route: string) => { setOpen(false); setActiveId(undefined); input.current?.blur(); navigate(route); };
  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "Escape") { event.preventDefault(); setOpen(false); setActiveId(undefined); return; }
    const options = () => Array.from(root.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
    if (event.key === "Enter") {
      event.preventDefault();
      const selected = expanded && activeId ? options().find(option => option.id === activeId) : undefined;
      if (selected) selected.click(); else submit();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault(); setOpen(true);
    const rows = options();
    if (!rows.length) return;
    const current = rows.findIndex(option => option.id === activeId);
    const index = event.key === "ArrowDown" ? (current + 1) % rows.length : (current <= 0 ? rows.length : current) - 1;
    setActiveId(rows[index].id);
    rows[index].scrollIntoView({ block: "nearest" });
  };
  return <div className="account-search" ref={root} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) { setOpen(false); setActiveId(undefined); }
  }}>
    <div className="account-search-input-wrap">
      <Search aria-hidden="true" />
      <input ref={input} type="search" role="combobox" aria-label={label} aria-autocomplete="list" aria-expanded={expanded} aria-controls={expanded ? `${id}-options` : undefined} aria-activedescendant={expanded ? activeId : undefined} autoComplete="off" placeholder={placeholder} value={query} onChange={event => change(event.target.value)} onFocus={() => setOpen(true)} onClick={() => setOpen(true)} onKeyDown={keyDown} />
      {!!query && <button type="button" className="account-search-clear" aria-label="Clear search" onClick={() => { change(""); input.current?.focus(); }}><X aria-hidden="true" /></button>}
    </div>
    {expanded && <div className="account-search-dropdown">
      <div className={cn(CAPTION, "account-search-heading")}>In your account</div>
      <div id={`${id}-options`} role="listbox" aria-label="Search suggestions" className="account-search-options">
        {lookupReady ? <Suggestions key={term} query={term} pageSize={pageSize} id={id} activeId={activeId} onActive={setActiveId} onChoose={choose} /> : <p className={cn(META, "account-search-status")} role="status">Searching…</p>}
        <Suggestion id={`${id}-all`} title="Search Stasht" kind={query} icon={Search} action activeId={activeId} onActive={setActiveId} onChoose={submit} />
      </div>
      <div className={cn(CAPTION, "account-search-key-hint")}><span>↑ ↓ to choose</span><span>Enter to open</span><span>Esc to close</span></div>
    </div>}
  </div>;
}

type SuggestionProps = { id: string; title: string; kind: string; icon: LucideIcon; image?: string; action?: boolean; activeId?: string; onActive: (id: string) => void; onChoose: () => void };
function Suggestion({ id, title, kind, icon: Icon, image, action, activeId, onActive, onChoose }: SuggestionProps) {
  return <button id={id} type="button" role="option" aria-selected={activeId === id} tabIndex={-1} className="account-search-option" data-action={action || undefined} onMouseDown={event => event.preventDefault()} onMouseEnter={() => onActive(id)} onClick={onChoose}>
    <span className="account-search-option-icon">{image ? <img src={image} alt="" /> : <Icon aria-hidden="true" />}</span>
    <span className="account-search-option-copy"><strong className={ROW_TITLE}>{title}</strong><small className={CAPTION}>{kind}</small></span>
    {action && <ArrowRight className="account-search-option-arrow" aria-hidden="true" />}
  </button>;
}
function StuffSuggestion({ entityId, ...props }: Omit<SuggestionProps, "title" | "kind" | "icon"> & { entityId: string }) {
  const entity = useEntityCard(entityId);
  return <Suggestion {...props} icon={Boxes} image={entityImageUrl(entity.data?.entity)} title={entity.data?.entity?.name || "Saved item"} kind={entity.isError ? "Stuff · Preview unavailable" : entity.isPending ? "Stuff · Loading preview…" : "Stuff"} />;
}

/** Existing caches own reads; mount only for a focused, active, debounced query. */
function Suggestions({ query, pageSize, id, activeId, onActive, onChoose }: { query: string; pageSize: number; id: string; activeId?: string; onActive: (id: string) => void; onChoose: (route: string) => void }) {
  const stashes = useSearchStashes(query, pageSize);
  const stuff = useStuffSearch(query);
  const { collections, tags, collectionsQuery, tagsQuery } = useSearchCatalogMatches(query);
  const posts = (stashes.data?.pages[0]?.posts ?? []).slice(0, 2);
  const stuffIds = stuff.ids.slice(0, 2);
  const matchedCollections = collections.slice(0, 2);
  const matchedTags = tags.slice(0, 2);
  const sources = [stashes, stuff, collectionsQuery, tagsQuery];
  const loading = sources.some(source => source.isFetching);
  const failed = sources.some(source => source.isError);
  const any = posts.length + stuffIds.length + matchedCollections.length + matchedTags.length > 0;
  const common = { activeId, onActive };
  return <>
    {matchedCollections.map(item => <Suggestion {...common} key={item.collection_id} id={`${id}-collection-${item.collection_id}`} title={collectionDisplayTitle(item.title)} kind="Collection" icon={Folder} onChoose={() => onChoose(COLLECTION_DETAIL({ collectionId: item.collection_id }))} />)}
    {matchedTags.map(item => <Suggestion {...common} key={item.id} id={`${id}-tag-${item.id}`} title={item.name} kind={isSuggestedTag(item) ? "Suggested tag" : "Tag"} icon={Tag} onChoose={() => onChoose(`${STASHES()}?tag=${encodeURIComponent(item.id)}`)} />)}
    {posts.map(post => <Suggestion {...common} key={post.id} id={`${id}-stash-${post.id}`} title={post.user_title || post.title || "Saved stash"} kind={post.platform ? `Stash · ${post.platform}` : "Stash"} icon={Bookmark} image={post.media.find(media => media.type === "image")?.url} onChoose={() => onChoose(STASH_DETAIL({ stashId: post.id }))} />)}
    {stuffIds.map(entityId => <StuffSuggestion {...common} key={entityId} id={`${id}-stuff-${entityId}`} entityId={entityId} onChoose={() => onChoose(STUFF_DETAIL({ entityId }))} />)}
    {loading && <p className={cn(META, "account-search-status")} role="status">Searching…</p>}
    {failed && <p className={cn(META, "account-search-status")} role="status">Some suggestions couldn’t load. Open all results to try again.</p>}
    {!loading && !failed && !any && <p className={cn(META, "account-search-status")} role="status">No suggestions found.</p>}
  </>;
}
